import React, { useState, useEffect } from 'react';
import { Package, Eye, Truck, CheckCircle, XCircle, Edit2, Trash2, FileText, FileSpreadsheet, ChevronDown, ChevronUp, Clock, Box, CreditCard, Receipt, Mail } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import axiosInstance from '../../api/axios';
import { ExportButton } from '../../components/admin/ExportButton';
import SendByEmailDialog from '../../components/SendByEmailDialog';
import { earliestShipByFromOrderItems, formatGermanDate } from '../../utils/deliveryTime';
import { OrderPaymentStatus } from '../../components/admin/OrderPaymentStatus';
import { OpenTransfersPanel } from '../../components/admin/OpenTransfersPanel';
import { formatEUR } from '../../utils/format';

// Reusable StatCard component with expandable order list - VERY LIGHT PASTEL colors
const StatCard = ({ title, count, icon, color, isExpanded, onToggle, orders = [], onSelectOrder, subtitle }) => {
  // Deutlichere Farbunterscheidung: Hintergrund-Tint, farbige Seitenleiste, Icon-Kontrast.
  // Auch bei heller Helligkeit gut unterscheidbar.
  const palette = {
    gray:   { bg: 'bg-slate-50',    border: 'border-slate-200',   bar: 'bg-slate-400',   iconBg: 'bg-slate-400',   text: 'text-slate-700',   count: 'text-slate-800',   hover: 'hover:border-slate-300 hover:shadow-slate-200/60' },
    yellow: { bg: 'bg-amber-50',    border: 'border-amber-200',   bar: 'bg-amber-400',   iconBg: 'bg-amber-400',   text: 'text-amber-700',   count: 'text-amber-800',   hover: 'hover:border-amber-300 hover:shadow-amber-200/60' },
    blue:   { bg: 'bg-sky-50',      border: 'border-sky-200',     bar: 'bg-sky-400',     iconBg: 'bg-sky-400',     text: 'text-sky-700',     count: 'text-sky-800',     hover: 'hover:border-sky-300 hover:shadow-sky-200/60' },
    purple: { bg: 'bg-pink-50',     border: 'border-pink-200',    bar: 'bg-pink-400',    iconBg: 'bg-pink-400',    text: 'text-pink-700',    count: 'text-pink-800',    hover: 'hover:border-pink-300 hover:shadow-pink-200/60' },
    green:  { bg: 'bg-emerald-50',  border: 'border-emerald-200', bar: 'bg-emerald-400', iconBg: 'bg-emerald-400', text: 'text-emerald-700', count: 'text-emerald-800', hover: 'hover:border-emerald-300 hover:shadow-emerald-200/60' },
  };
  const p = palette[color] || palette.gray;

  return (
    <div className="relative">
      <Card 
        className={`cursor-pointer transition-all duration-200 rounded-none border-2 hover:shadow-md relative overflow-hidden ${p.bg} ${p.border} ${p.hover} group`}
        onClick={onToggle}
        data-testid={`stat-card-${color}`}
      >
        {/* Farbige linke Seitenleiste — deutliche Unterscheidung */}
        <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${p.bar}`} />
        <CardContent className="p-3 sm:p-4 pl-4 sm:pl-5">
          {/* Icon + Title */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 flex items-center justify-center text-white ${p.iconBg}`}>{icon}</div>
              <span className={`text-xs sm:text-sm font-semibold ${p.text}`}>{title}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-[9px] uppercase tracking-widest text-gray-400 hidden md:inline opacity-0 group-hover:opacity-100 transition-opacity">
                Klicken
              </span>
              {isExpanded ? (
                <ChevronUp className={`h-4 w-4 ${p.text}`} />
              ) : (
                <ChevronDown className={`h-4 w-4 ${p.text}`} />
              )}
            </div>
          </div>
          {/* Number */}
          <div className={`text-2xl sm:text-3xl font-bold ${p.count}`}>{count}</div>
          {subtitle && <div className="text-xs text-gray-500 mt-1">{subtitle}</div>}
        </CardContent>
      </Card>
      
      {/* Expanded dropdown with order list */}
      {isExpanded && orders.length > 0 && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border-2 border-gray-200 rounded-none shadow-lg max-h-64 overflow-y-auto">
          <div className="p-2 bg-gray-50 border-b text-xs font-medium text-gray-500">
            {orders.length} Bestellung(en)
          </div>
          {orders.slice(0, 10).map((order) => (
            <div
              key={order.id}
              className="p-3 border-b hover:bg-gray-50 cursor-pointer transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onSelectOrder(order);
              }}
            >
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium text-sm">#{order.id?.slice(0, 8)}</p>
                  <p className="text-xs text-gray-500">
                    {order.shipping_address?.first_name} {order.shipping_address?.last_name}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-sm">{formatEUR((order.total_amount || 0))}</p>
                  <p className="text-xs text-gray-400">
                    {new Date(order.created_at).toLocaleDateString('de-DE')}
                  </p>
                </div>
              </div>
            </div>
          ))}
          {orders.length > 10 && (
            <div className="p-2 text-center text-xs text-gray-500 bg-gray-50">
              +{orders.length - 10} weitere Bestellungen
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const OrdersManager = () => {
  const [orders, setOrders] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingStatus, setEditingStatus] = useState(false);
  const [statusData, setStatusData] = useState({ status: '', tracking_number: '' });
  const [expandedStatCard, setExpandedStatCard] = useState(null); // Track which stat card is expanded

  // Email-Dialog state
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailFiles, setEmailFiles] = useState([]);
  const [emailDefaults, setEmailDefaults] = useState({ subject: '', message: '', recipient: '', vars: {} });
  
  // Date filters
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonthFrom, setSelectedMonthFrom] = useState(null);
  const [selectedMonthTo, setSelectedMonthTo] = useState(null);
  
  // Archive + bulk selection state (analog zu DocumentsManager)
  const [archiveMode, setArchiveMode] = useState('active'); // 'active', 'archived', 'all'
  const [showMode, setShowMode] = useState('real'); // 'real' | 'test' | 'all' — filter test vs real orders
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedIds, setSelectedIds] = useState({}); // { orderId: true }
  const [bulkBusy, setBulkBusy] = useState(false);
  
  const months = [
    { value: 1, label: 'Januar' },
    { value: 2, label: 'Februar' },
    { value: 3, label: 'März' },
    { value: 4, label: 'April' },
    { value: 5, label: 'Mai' },
    { value: 6, label: 'Juni' },
    { value: 7, label: 'Juli' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'Oktober' },
    { value: 11, label: 'November' },
    { value: 12, label: 'Dezember' }
  ];
  
  // Generate years from 2026 onwards
  const years = [];
  for (let y = 2026; y <= currentYear + 1; y++) {
    years.push(y);
  }

  useEffect(() => {
    loadOrders();
  }, [archiveMode, showMode]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const qs = [];
      if (archiveMode === 'archived') qs.push('only_archived=true');
      else if (archiveMode === 'all') qs.push('include_archived=true');
      qs.push(`show=${showMode}`);
      const params = qs.length ? `?${qs.join('&')}` : '';
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/admin/all${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders);
        setStatistics(data.statistics);
        setSelectedIds({}); // Auswahl beim Wechsel zurücksetzen
      }
    } catch (error) {
      console.error('Error loading orders:', error);
      toast.error('Fehler beim Laden der Bestellungen');
    } finally {
      setLoading(false);
    }
  };

  // ---------- Bulk-Auswahl helpers ----------
  const toggleSelect = (orderId) => {
    setSelectedIds((prev) => ({ ...prev, [orderId]: !prev[orderId] }));
  };

  const selectAllVisible = (orders) => {
    const allSelected = orders.length > 0 && orders.every((o) => selectedIds[o.id]);
    if (allSelected) {
      setSelectedIds({});
    } else {
      const next = {};
      orders.forEach((o) => { next[o.id] = true; });
      setSelectedIds(next);
    }
  };

  const selectedCount = Object.values(selectedIds).filter(Boolean).length;
  const selectedOrderIds = () => Object.keys(selectedIds).filter((k) => selectedIds[k]);

  const bulkArchive = async () => {
    if (!selectedCount) return toast.error('Bitte mind. eine Bestellung auswählen');
    if (!window.confirm(`${selectedCount} Bestellung(en) ins Archiv verschieben?\n\nDie zugehörigen Rechnungen werden ebenfalls archiviert. Alles bleibt jederzeit über „Nur Archiv" wiederherstellbar.`)) return;
    try {
      setBulkBusy(true);
      const docs = selectedOrderIds().map((id) => ({ type: 'order_confirmation', id }));
      const r = await axiosInstance.post('/documents/admin/bulk-archive', { documents: docs });
      toast.success(r.data.message);
      loadOrders();
    } catch (e) {
      toast.error('Archivierung fehlgeschlagen');
    } finally { setBulkBusy(false); }
  };

  const bulkRestore = async () => {
    if (!selectedCount) return toast.error('Bitte mind. eine Bestellung auswählen');
    try {
      setBulkBusy(true);
      const docs = selectedOrderIds().map((id) => ({ type: 'order_confirmation', id }));
      const r = await axiosInstance.post('/documents/admin/bulk-archive', { documents: docs, restore: true });
      toast.success(r.data.message);
      loadOrders();
    } catch (e) {
      toast.error('Wiederherstellung fehlgeschlagen');
    } finally { setBulkBusy(false); }
  };

  const setPresetRange = (preset) => {
    const today = new Date();
    let from, to;
    if (preset === 'this_month') {
      from = new Date(today.getFullYear(), today.getMonth(), 1);
      to = today;
    } else if (preset === 'last_month') {
      from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      to = new Date(today.getFullYear(), today.getMonth(), 0);
    } else if (preset === 'this_year') {
      from = new Date(today.getFullYear(), 0, 1);
      to = today;
    } else {
      from = null; to = null;
    }
    setFromDate(from ? from.toISOString().slice(0, 10) : '');
    setToDate(to ? to.toISOString().slice(0, 10) : '');
  };

  // ====== PDF helpers ======
  const downloadOrderPdf = async (orderId, kind, orderNumber) => {
    const urlMap = {
      confirmation: `/documents/order/${orderId}/confirmation/pdf`,
      delivery_note: `/documents/order/${orderId}/delivery-note/pdf`,
    };
    const labels = { confirmation: 'Bestellbestaetigung', delivery_note: 'Lieferschein' };
    try {
      const res = await axiosInstance.get(urlMap[kind], { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `${labels[kind]}_${orderNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast.error('Fehler beim Download');
    }
  };

  const downloadInvoicePdfForOrder = async (orderId, orderNumber) => {
    try {
      const inv = await axiosInstance.get(`/invoices/admin/order/${orderId}`);
      const invoiceId = inv.data?.id || inv.data?.invoice?.id;
      const invNum = inv.data?.invoice_number || inv.data?.invoice?.invoice_number || orderNumber;
      if (!invoiceId) {
        toast.error('Für diese Bestellung wurde noch keine Rechnung erstellt');
        return;
      }
      const res = await axiosInstance.get(`/documents/invoice/${invoiceId}/pdf`, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `Rechnung_${invNum}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast.error('Rechnung konnte nicht geladen werden');
    }
  };

  const openOrderEmailDialog = async (order) => {
    const orderNumber = order.order_number;
    const customerEmail = order.user_email || order.billing_address?.email || '';
    const customerName = `${order.billing_address?.first_name || ''} ${order.billing_address?.last_name || ''}`.trim();

    // Build attachments — try to also include invoice if it exists for this order
    const attachments = [
      {
        filename: `Bestellbestaetigung_${orderNumber}.pdf`,
        getBlob: async () => {
          const r = await axiosInstance.get(`/documents/order/${order.id}/confirmation/pdf`, { responseType: 'blob' });
          return new Blob([r.data], { type: 'application/pdf' });
        },
      },
      {
        filename: `Lieferschein_${orderNumber}.pdf`,
        getBlob: async () => {
          const r = await axiosInstance.get(`/documents/order/${order.id}/delivery-note/pdf`, { responseType: 'blob' });
          return new Blob([r.data], { type: 'application/pdf' });
        },
      },
    ];

    try {
      const inv = await axiosInstance.get(`/invoices/admin/order/${order.id}`);
      const invoiceId = inv.data?.id || inv.data?.invoice?.id;
      const invNum = inv.data?.invoice_number || inv.data?.invoice?.invoice_number;
      if (invoiceId) {
        attachments.unshift({
          filename: `Rechnung_${invNum || orderNumber}.pdf`,
          getBlob: async () => {
            const r = await axiosInstance.get(`/documents/invoice/${invoiceId}/pdf`, { responseType: 'blob' });
            return new Blob([r.data], { type: 'application/pdf' });
          },
        });
      }
    } catch (_) { /* keine Rechnung -> Dokumente bleiben */ }

    setEmailFiles(attachments);
    setEmailDefaults({
      recipient: customerEmail,
      subject: `Ihre Bestellung #${orderNumber} bei Homeyoursoul`,
      message: `Sehr geehrte/r ${customerName || 'Kundin / Kunde'},\n\nanbei senden wir Ihnen die Dokumente zu Ihrer Bestellung #${orderNumber}.\n\nHerzliche Grüße\nYasemin Kurt-Yilmaz\nHomeyoursoul`,
      vars: { Kundenname: customerName, Rechnungsnummer: '' },
    });
    setEmailOpen(true);
  };

  const loadOrderDetails = async (orderId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/admin/${orderId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedOrder(data);
      }
    } catch (error) {
      console.error('Error loading order details:', error);
      toast.error('Fehler beim Laden der Details');
    }
  };

  const updateOrderStatus = async (orderId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/admin/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ ...statusData, status: statusData.status || selectedOrder?.order?.status })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setEditingStatus(false);
        loadOrders();
        if (selectedOrder) {
          loadOrderDetails(orderId);
        }
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const deleteOrder = async (orderId, isTest) => {
    if (!isTest) {
      toast.error('Echte Bestellungen können aus rechtlichen Gründen nicht gelöscht werden (10-jährige Aufbewahrungspflicht). Bitte stattdessen archivieren.');
      return;
    }
    if (!window.confirm('Testbestellung wirklich löschen?\n\nTestbestellungen enthalten keine Geldtransfers und sind keine rechtlich relevanten Dokumente.')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/admin/${orderId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        toast.success('Testbestellung gelöscht');
        setSelectedOrder(null);
        loadOrders();
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.detail || 'Löschen nicht möglich');
      }
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  // Toggle is_test-Flag einer Bestellung (Admin markiert eigene Testkäufe manuell)
  const toggleTestFlag = async (orderId, currentIsTest) => {
    const nextIsTest = !currentIsTest;
    const msg = nextIsTest
      ? 'Diese Bestellung als TEST markieren?\n\nSie erscheint dann nicht mehr in Umsatz-Statistiken und kann anschließend über „Tests archivieren" gebündelt entfernt werden.'
      : 'Test-Markierung entfernen?\n\nDie Bestellung wird wieder als reguläre Bestellung gezählt.';
    if (!window.confirm(msg)) return;
    try {
      const r = await axiosInstance.patch(`/orders/admin/${orderId}/mark-test`, { is_test: nextIsTest });
      toast.success(r.data.message || 'Aktualisiert');
      setSelectedOrder(null);
      loadOrders();
    } catch (e) {
      toast.error('Aktualisierung fehlgeschlagen');
    }
  };

  // Bulk-Cleanup Waisen-Bestellungen (kein order_number/email/total)
  const cleanupOrphanOrders = async () => {
    try {
      const dry = await axiosInstance.post('/orders/admin/cleanup-orphans?dry_run=true');
      const n = dry.data.would_delete || 0;
      if (!n) {
        toast.info('Keine Waisen-Bestellungen gefunden. Alles sauber! ✓');
        return;
      }
      const preview = (dry.data.preview || []).slice(0, 10)
        .map(p => `• ${(p.created_at || '').slice(0,10)} · Status: ${p.status || 'unbekannt'}`)
        .join('\n');
      const ok = window.confirm(
        `${n} Waisen-Bestellung(en) endgültig löschen?\n\n${preview}${n > 10 ? `\n…und ${n - 10} weitere` : ''}\n\nDiese Einträge haben KEINE Bestellnummer, KEINE E-Mail und KEINEN Betrag — reine Test-/Import-Reste. Aktion ist nicht umkehrbar.`
      );
      if (!ok) return;
      const res = await axiosInstance.post('/orders/admin/cleanup-orphans?dry_run=false');
      toast.success(res.data.message || `${res.data.deleted} gelöscht`);
      loadOrders();
    } catch (e) {
      toast.error('Bereinigung fehlgeschlagen');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: 'Ausstehend', color: 'bg-yellow-100 text-yellow-800' },
      processing: { label: 'In Bearbeitung', color: 'bg-blue-100 text-blue-800' },
      shipped: { label: 'Versendet', color: 'bg-purple-100 text-purple-800' },
      delivered: { label: 'Zugestellt', color: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Storniert', color: 'bg-red-100 text-red-800' }
    };
    const c = config[status] || config.pending;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  // Filter orders by status and date
  const filteredOrders = orders.filter(order => {
    // Status filter
    if (statusFilter !== 'all' && order.status !== statusFilter) {
      return false;
    }
    
    // Date filter (year/month picker — bestehende UI)
    if (selectedYear) {
      const orderDate = new Date(order.created_at);
      const orderYear = orderDate.getFullYear();
      const orderMonth = orderDate.getMonth() + 1; // 1-12
      
      if (orderYear !== selectedYear) {
        return false;
      }
      
      if (selectedMonthFrom && orderMonth < selectedMonthFrom) {
        return false;
      }
      
      if (selectedMonthTo && orderMonth > selectedMonthTo) {
        return false;
      }
    }
    
    // Date range filter (neu: explizites von/bis)
    if (fromDate && order.created_at < fromDate) return false;
    if (toDate && order.created_at > `${toDate}T23:59:59`) return false;
    
    return true;
  });

  if (loading) {
    return <div className="p-8">Lade Bestellungen...</div>;
  }

  return (
    <div className="min-h-screen py-4 sm:py-6 lg:py-8 px-3 sm:px-4 md:px-6 lg:px-8" style={{ backgroundColor: '#F5EDE4' }}>
      <div className="max-w-7xl mx-auto">
        {/* Goldbraune Überschrift-Leiste */}
        <div className="mb-4 p-3 md:p-4 flex items-center gap-3" style={{ backgroundColor: '#C4A084' }}>
          <div className="w-10 h-10 rounded flex items-center justify-center bg-white/90">
            <Package className="h-5 w-5" style={{ color: '#C4A084' }} />
          </div>
          <div>
            <h1 className="text-lg md:text-xl font-bold text-white">Bestellverwaltung</h1>
            <p className="text-xs md:text-sm text-white/80">Verwalten Sie alle Kundenbestellungen</p>
          </div>
        </div>

        {/* Section: Status Overview */}
        <div className="mb-3 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
          <div className="w-6 h-6 rounded flex items-center justify-center bg-[#FFF8F0]">
            <Package className="h-4 w-4 text-[#E89860]" />
          </div>
          <h2 className="text-base font-semibold text-white">Status-Übersicht</h2>
        </div>

        {/* Interactive Statistics Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-6">
          {/* All Orders Card */}
          <StatCard
            title="Gesamt"
            count={statistics.total || 0}
            icon={<Package className="h-5 w-5" />}
            color="gray"
            isExpanded={expandedStatCard === 'all'}
            onToggle={() => {
              setExpandedStatCard(expandedStatCard === 'all' ? null : 'all');
              setStatusFilter('all');
            }}
            orders={orders}
            onSelectOrder={(order) => {
              loadOrderDetails(order.id);
              setExpandedStatCard(null);
            }}
          />
          
          {/* Pending Orders */}
          <StatCard
            title="Ausstehend"
            count={statistics.pending || 0}
            icon={<Clock className="h-5 w-5" />}
            color="yellow"
            isExpanded={expandedStatCard === 'pending'}
            onToggle={() => {
              setExpandedStatCard(expandedStatCard === 'pending' ? null : 'pending');
              setStatusFilter('pending');
            }}
            orders={orders.filter(o => o.status === 'pending')}
            onSelectOrder={(order) => {
              loadOrderDetails(order.id);
              setExpandedStatCard(null);
            }}
          />
          
          {/* Processing Orders */}
          <StatCard
            title="In Bearbeitung"
            count={statistics.processing || 0}
            icon={<Box className="h-5 w-5" />}
            color="blue"
            isExpanded={expandedStatCard === 'processing'}
            onToggle={() => {
              setExpandedStatCard(expandedStatCard === 'processing' ? null : 'processing');
              setStatusFilter('processing');
            }}
            orders={orders.filter(o => o.status === 'processing')}
            onSelectOrder={(order) => {
              loadOrderDetails(order.id);
              setExpandedStatCard(null);
            }}
          />
          
          {/* Shipped Orders */}
          <StatCard
            title="Versendet"
            count={statistics.shipped || 0}
            icon={<Truck className="h-5 w-5" />}
            color="purple"
            isExpanded={expandedStatCard === 'shipped'}
            onToggle={() => {
              setExpandedStatCard(expandedStatCard === 'shipped' ? null : 'shipped');
              setStatusFilter('shipped');
            }}
            orders={orders.filter(o => o.status === 'shipped')}
            onSelectOrder={(order) => {
              loadOrderDetails(order.id);
              setExpandedStatCard(null);
            }}
          />
          
          {/* Revenue Card */}
          <StatCard
            title="Umsatz"
            count={`${formatEUR((statistics.total_revenue || 0))}`}
            icon={<CreditCard className="h-5 w-5" />}
            color="green"
            isExpanded={expandedStatCard === 'revenue'}
            onToggle={() => {
              setExpandedStatCard(expandedStatCard === 'revenue' ? null : 'revenue');
              setStatusFilter('all');
            }}
            orders={orders.filter(o => o.status === 'delivered')}
            onSelectOrder={(order) => {
              loadOrderDetails(order.id);
              setExpandedStatCard(null);
            }}
            subtitle="Zugestellte Bestellungen"
          />
        </div>

        {/* Test Orders Info */}
        {statistics.test_orders_count > 0 && (
          <Card className="mb-6 bg-orange-50 border-orange-200 rounded-none">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Badge className="bg-orange-100 text-orange-800">TEST</Badge>
                <span className="text-sm text-orange-900">
                  <strong>{statistics.test_orders_count}</strong> Test-Bestellung(en) werden nicht im Umsatz gezählt. 
                  Diese wurden vor dem offiziellen Shop-Launch erstellt.
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section: Filter */}
        <h2 className="text-base font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Clock className="h-4 w-4 text-[#8B7355]" />
          Zeitraum filtern
        </h2>

        {/* Date Filters */}
        <Card className="mb-6 rounded-none">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-3">
              <Label className="font-semibold text-sm">Zeitraum:</Label>
            
            {/* Year */}
            <Select value={selectedYear?.toString() || ''} onValueChange={(v) => setSelectedYear(v ? parseInt(v) : null)}>
              <SelectTrigger className="w-24">
                <SelectValue placeholder="Jahr" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle</SelectItem>
                {years.map(y => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Month From */}
            <Select value={selectedMonthFrom?.toString() || ''} onValueChange={(v) => setSelectedMonthFrom(v && v !== 'all' ? parseInt(v) : null)}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Von Monat" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Monate</SelectItem>
                {months.map(m => (
                  <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <span className="text-gray-400">bis</span>
            
            {/* Month To */}
            <Select value={selectedMonthTo?.toString() || ''} onValueChange={(v) => setSelectedMonthTo(v && v !== 'all' ? parseInt(v) : null)}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Bis Monat" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Monate</SelectItem>
                {months.map(m => (
                  <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => {
                setSelectedYear(currentYear);
                setSelectedMonthFrom(null);
                setSelectedMonthTo(null);
                setFromDate('');
                setToDate('');
              }}
            >
              Zurücksetzen
            </Button>
            </div>
            
            {/* Erweitert: Datums-Range + Schnellauswahl + Archiv-Modus */}
            <div className="flex flex-wrap items-center gap-2 pt-3 mt-3 border-t border-[#E8DCC8]">
              <Label className="text-xs font-semibold text-[#5D4E3C]">Genauer Zeitraum:</Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="h-9 w-auto text-sm rounded-none"
                aria-label="Von Datum"
                data-testid="orders-from-date"
              />
              <span className="text-xs text-[#8B7355]">bis</span>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="h-9 w-auto text-sm rounded-none"
                aria-label="Bis Datum"
                data-testid="orders-to-date"
              />
              <button onClick={() => setPresetRange('this_month')} className="text-xs px-2 py-1 bg-[#FAF5EE] hover:bg-[#F5EDE4] border border-[#D4C4B0] rounded" data-testid="orders-preset-this-month">Aktueller Monat</button>
              <button onClick={() => setPresetRange('last_month')} className="text-xs px-2 py-1 bg-[#FAF5EE] hover:bg-[#F5EDE4] border border-[#D4C4B0] rounded" data-testid="orders-preset-last-month">Letzter Monat</button>
              <button onClick={() => setPresetRange('this_year')} className="text-xs px-2 py-1 bg-[#FAF5EE] hover:bg-[#F5EDE4] border border-[#D4C4B0] rounded" data-testid="orders-preset-this-year">Dieses Jahr</button>
              
              <div className="ml-auto flex items-center gap-2">
                <Label className="text-xs font-semibold text-[#5D4E3C]">Anzeigen:</Label>
                <select
                  value={archiveMode}
                  onChange={(e) => setArchiveMode(e.target.value)}
                  className="h-9 px-2 text-sm border border-[#D4C4B0] bg-white rounded-none"
                  data-testid="orders-archive-mode"
                  title="Welche Bestellungen anzeigen"
                >
                  <option value="active">Aktive</option>
                  <option value="archived">Nur Archiv</option>
                  <option value="all">Alle (inkl. Archiv)</option>
                </select>
              </div>
            </div>

            {/* Real / Test / All tabs — test orders are isolated by default
                so the dashboard only shows real customer business. */}
            <div className="mt-3 flex flex-wrap items-center gap-2 pt-3 border-t border-[#EFE5D7]" data-testid="orders-show-tabs">
              <span className="text-xs font-semibold text-[#5D4E3C] mr-1">Bestellungstyp:</span>
              {[
                { key: 'real', label: 'Echte Bestellungen', count: statistics.total },
                { key: 'test', label: 'Testbestellungen', count: statistics.test_orders_count },
                { key: 'all', label: 'Alle', count: null },
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setShowMode(tab.key)}
                  data-testid={`orders-show-${tab.key}`}
                  className={`px-3 py-1.5 text-xs font-medium border transition-colors ${
                    showMode === tab.key
                      ? 'bg-[#5D4E3C] text-white border-[#5D4E3C]'
                      : 'bg-white text-[#5D4E3C] border-[#D4C4B0] hover:bg-[#FAF5EE]'
                  }`}
                >
                  {tab.label}
                  {tab.count != null && (
                    <span className={`ml-1.5 ${showMode === tab.key ? 'opacity-80' : 'text-[#9C8C7A]'}`}>
                      ({tab.count})
                    </span>
                  )}
                </button>
              ))}
              <span className="text-[11px] text-[#9C8C7A] ml-auto">
                Testbestellungen zählen nicht zum Umsatz oder zur Bestellanzahl.
              </span>
            </div>

            {/* Datenbereinigung — sichere Aktionen für Produktions-Cleanup */}
            <div className="mt-3 flex flex-wrap items-center gap-2 pt-3 border-t border-[#EFE5D7]" data-testid="orders-cleanup-toolbar">
              <span className="text-xs font-semibold text-[#5D4E3C] mr-1">🧹 Datenbereinigung:</span>
              <Button
                size="sm"
                variant="outline"
                onClick={cleanupOrphanOrders}
                className="rounded-none border-red-300 text-red-700 hover:bg-red-50 h-8 text-xs"
                title="Löscht Bestellungen ohne Nummer, ohne Email und ohne Betrag (reine Test-Reste)"
                data-testid="orders-cleanup-orphans"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" />
                Waisen-Bestellungen bereinigen
              </Button>
              <span className="text-[11px] text-[#9C8C7A]">
                Findet Einträge ohne Nummer/Email/Betrag mit Vorschau vor dem Löschen.
              </span>
            </div>
          </CardContent>
        </Card>
        
        {/* Bulk-Aktions-Toolbar (sticky, erscheint nur bei Auswahl) */}
        {selectedCount > 0 && (
          <Card className="mb-4 border-[#C4A084] border-2 rounded-none" data-testid="orders-bulk-toolbar">
            <CardContent className="p-3 flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-[#5D4E3C]">
                <strong>{selectedCount}</strong> Bestellung(en) ausgewählt
              </span>
              <div className="flex-1" />
              <Button size="sm" variant="outline" onClick={() => setSelectedIds({})} className="rounded-none border-[#D4C4B0]" data-testid="orders-bulk-clear">
                Auswahl löschen
              </Button>
              {archiveMode === 'archived' ? (
                <Button size="sm" variant="outline" onClick={bulkRestore} disabled={bulkBusy} className="rounded-none border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="orders-bulk-restore">
                  ↩ Wiederherstellen
                </Button>
              ) : (
                <Button size="sm" onClick={bulkArchive} disabled={bulkBusy} className="rounded-none bg-[#8B6F4D] hover:bg-[#5D4E3C] text-white" data-testid="orders-bulk-archive">
                  📦 Auswahl archivieren
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        <OpenTransfersPanel refreshKey={orders} onOpenOrder={loadOrderDetails} />

        {/* Section: Order List */}
        <h2 className="text-base font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <FileText className="h-4 w-4 text-[#8B7355]" />
          Bestellliste
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Orders List */}
          <div className="lg:col-span-2">
            <Card className="rounded-none">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={filteredOrders.length > 0 && filteredOrders.every((o) => selectedIds[o.id])}
                      onChange={() => selectAllVisible(filteredOrders)}
                      className="h-4 w-4 accent-[#C4A084] cursor-pointer"
                      title="Alle sichtbaren auswählen / abwählen"
                      aria-label="Alle Bestellungen auswählen"
                      data-testid="orders-select-all"
                    />
                    <CardTitle>Bestellungen ({filteredOrders.length})</CardTitle>
                  </div>
                <div className="flex items-center gap-2">
                  <ExportButton 
                    exportType="orders"
                    label="Excel Export"
                    filters={{ 
                      status: statusFilter !== 'all' ? statusFilter : undefined,
                      year: selectedYear || undefined,
                      month_from: selectedMonthFrom || undefined,
                      month_to: selectedMonthTo || undefined
                    }}
                    size="sm"
                  />
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-36 sm:w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle</SelectItem>
                      <SelectItem value="pending">Ausstehend</SelectItem>
                      <SelectItem value="processing">In Bearbeitung</SelectItem>
                      <SelectItem value="shipped">Versendet</SelectItem>
                      <SelectItem value="delivered">Zugestellt</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {filteredOrders.map((order) => (
                  <div
                    key={order.id}
                    className={`p-4 border rounded hover:bg-gray-50 transition-colors ${
                      selectedOrder?.order.id === order.id ? 'border-blue-500 bg-blue-50' : ''
                    } ${order.is_test ? 'border-l-4 border-l-orange-400 bg-orange-50/30' : ''} ${order.archived_at ? 'opacity-70 bg-gray-50' : ''}`}
                    data-testid={`orders-row-${order.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={!!selectedIds[order.id]}
                        onChange={(e) => { e.stopPropagation(); toggleSelect(order.id); }}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-1 h-4 w-4 accent-[#C4A084] cursor-pointer flex-shrink-0"
                        aria-label={`Bestellung #${order.order_number} auswählen`}
                        data-testid={`orders-select-${order.id}`}
                      />
                      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => loadOrderDetails(order.id)}>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-bold">#{order.order_number}</p>
                              {order.is_test && (
                                <Badge className="bg-orange-100 text-orange-800 text-xs">TEST</Badge>
                              )}
                              {order.archived_at && (
                                <Badge className="bg-gray-200 text-gray-700 text-xs border border-gray-300" title={`Archiviert am ${order.archived_at.slice(0,10)}`} data-testid={`orders-archive-badge-${order.id}`}>
                                  📦 Archiv
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 truncate">{order.user_email}</p>
                          </div>
                          {getStatusBadge(order.status)}
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">
                            {new Date(order.created_at).toLocaleDateString('de-DE')}
                          </span>
                          <span className="font-semibold">{formatEUR(order.total)}</span>
                        </div>
                        {/* Versand-Deadline für den Admin */}
                        {!['shipped', 'delivered', 'cancelled', 'returned'].includes(order.status) && (() => {
                          const deadline = earliestShipByFromOrderItems(order.created_at || new Date(), order.items || []);
                          const today = new Date(); today.setHours(0,0,0,0);
                          const deadlineMid = new Date(deadline); deadlineMid.setHours(0,0,0,0);
                          const daysLeft = Math.round((deadlineMid - today) / 86400000);
                          const isOverdue = daysLeft < 0;
                          const isUrgent = !isOverdue && daysLeft <= 1;
                          return (
                            <p
                              className={`text-[11px] mt-1 inline-flex items-center gap-1 px-2 py-0.5 border ${
                                isOverdue
                                  ? 'bg-red-50 border-red-300 text-red-800'
                                  : isUrgent
                                    ? 'bg-amber-50 border-amber-300 text-amber-900'
                                    : 'bg-[#FAF5EE] border-[#E8DCC8] text-[#5D4E3C]'
                              }`}
                              data-testid={`order-ship-by-${order.id}`}
                            >
                              🚚 <strong>Versand bis:</strong>&nbsp;{formatGermanDate(deadline)}
                              {isOverdue
                                ? ` · ⚠ überfällig (${Math.abs(daysLeft)} Tag${Math.abs(daysLeft) === 1 ? '' : 'e'})`
                                : daysLeft === 0
                                  ? ' · heute!'
                                  : ` · in ${daysLeft} Tag${daysLeft === 1 ? '' : 'en'}`}
                            </p>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Order Details */}
        <div>
          {selectedOrder ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Details</span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditingStatus(!editingStatus)}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    {/* Delete is only shown for test orders. Real orders
                        must be archived (legal: 10-yr retention §147 AO). */}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => toggleTestFlag(selectedOrder.order.id, selectedOrder.order.is_test)}
                      title={selectedOrder.order.is_test ? 'Test-Markierung entfernen' : 'Als Testbestellung markieren'}
                      data-testid="toggle-test-flag-btn"
                      className={selectedOrder.order.is_test ? 'text-orange-600 hover:bg-orange-50' : 'text-gray-500 hover:bg-gray-100'}
                    >
                      <span className="text-xs font-semibold">
                        {selectedOrder.order.is_test ? '✓ TEST' : '⚑ als Test'}
                      </span>
                    </Button>
                    {selectedOrder.order.is_test && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteOrder(selectedOrder.order.id, true)}
                        title="Testbestellung löschen"
                        data-testid="delete-test-order-btn"
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-medium mb-1">Bestellnummer</p>
                  <p className="text-sm">#{selectedOrder.order.order_number}</p>
                </div>

                <OrderPaymentStatus
                  order={selectedOrder.order}
                  onPaid={() => { loadOrders(); loadOrderDetails(selectedOrder.order.id); }}
                />

                <div>
                  <p className="text-sm font-medium mb-1">Status</p>
                  {editingStatus ? (
                    <div className="space-y-3">
                      <Select
                        value={statusData.status || selectedOrder.order.status}
                        onValueChange={(val) => setStatusData({...statusData, status: val})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Ausstehend</SelectItem>
                          <SelectItem value="processing">In Bearbeitung</SelectItem>
                          <SelectItem value="shipped">Versendet</SelectItem>
                          <SelectItem value="delivered">Zugestellt</SelectItem>
                          <SelectItem value="cancelled">Storniert</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        data-testid="order-tracking-number-input"
                        placeholder="Sendungsnummer (optional)"
                        value={statusData.tracking_number}
                        onChange={(e) => setStatusData({...statusData, tracking_number: e.target.value})}
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => updateOrderStatus(selectedOrder.order.id)} className="rounded-none bg-[#C4A484] hover:bg-[#B89968]">
                          Speichern
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingStatus(false)} className="rounded-none border-[#D4C4B0]">
                          Abbrechen
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {getStatusBadge(selectedOrder.order.status)}
                      {selectedOrder.order.tracking_number && (
                        <p className="text-sm mt-2">Sendung: {selectedOrder.order.tracking_number}</p>
                      )}
                    </>
                  )}
                </div>

                <div>
                  <p className="text-sm font-medium mb-2">Kunde</p>
                  <div className="text-sm space-y-1">
                    <p>{selectedOrder.customer?.first_name} {selectedOrder.customer?.last_name}</p>
                    <p className="text-gray-600">{selectedOrder.order.user_email}</p>
                  </div>
                  {selectedOrder.customer?.admin_note && (
                    <div
                      className="mt-2 p-2 bg-[#FAF5EE] border border-[#D4C4B0] text-xs text-[#5D4E3C]"
                      data-testid="order-customer-admin-note"
                    >
                      <div className="flex items-center gap-1 mb-1">
                        <span>📝</span>
                        <strong className="text-[11px] uppercase tracking-wide">Meine Notiz zu diesem Kunden</strong>
                      </div>
                      <p className="whitespace-pre-wrap italic">{selectedOrder.customer.admin_note}</p>
                    </div>
                  )}
                </div>

                <div>
                  <p className="text-sm font-medium mb-2">Artikel</p>
                  <div className="space-y-2">
                    {selectedOrder.order.items.map((item, i) => (
                      <div key={i} className="text-sm">
                        <div className="flex justify-between">
                          <span>{item.product_name} (x{item.quantity})</span>
                          <span>{formatEUR((item.price * item.quantity))}</span>
                        </div>
                        {item.delivery_time && (
                          <div
                            className={`mt-1 text-[11px] inline-flex items-center gap-1 px-1.5 py-0.5 ${
                              item.delivery_time === '3-7'
                                ? 'text-amber-900 bg-amber-50 border border-amber-200'
                                : 'text-[#5D8A5A] bg-[#F4FAEF] border border-[#D9E8C9]'
                            }`}
                            data-testid={`order-item-delivery-${i}`}
                          >
                            🚚 {item.delivery_time === '3-7' ? '3–7 WT · auf Bestellung' : '1–3 WT · auf Lager'}
                          </div>
                        )}
                        {item.gift_label_text && (
                          <div
                            className="mt-1 text-[11px] flex items-center gap-1.5 px-2 py-1 border border-dashed border-[#C4A084] bg-[#FAF5EE]"
                            data-testid={`order-item-gift-label-${i}`}
                          >
                            <span style={{ color: '#C4A084' }}>♡</span>
                            <span className="text-[#5D4E3C]">
                              <strong>Etikett:</strong> {item.gift_label_text}
                            </span>
                          </div>
                        )}
                        {item.gift_note && (
                          <div
                            className="mt-1 text-[11px] flex items-start gap-1.5 px-2 py-1 bg-amber-50 border border-amber-200 text-amber-900"
                            data-testid={`order-item-gift-note-${i}`}
                          >
                            <span>📝</span>
                            <span><strong>Bemerkung (intern):</strong> {item.gift_note}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t pt-3">
                  <div className="flex justify-between text-sm font-bold">
                    <span>Gesamt:</span>
                    <span>{formatEUR(selectedOrder.order.total)}</span>
                  </div>
                </div>

                {/* Zeitleiste der Bestellung */}
                <div className="border-t pt-3">
                  <p className="text-sm font-medium mb-2">🕐 Zeitleiste</p>
                  <div className="space-y-1.5 pl-1" data-testid="order-timeline">
                    {(() => {
                      const o = selectedOrder.order;
                      const events = [];
                      if (o.created_at) events.push({ icon: '🛒', label: 'Bestellung eingegangen', at: o.created_at });
                      if (o.paid_at) events.push({ icon: '💳', label: 'Zahlung erhalten', at: o.paid_at });
                      else if (o.payment_status === 'paid' && !o.paid_at) events.push({ icon: '💳', label: 'Zahlung erhalten (Zeitpunkt unbekannt)', at: null });
                      if (o.processing_at) events.push({ icon: '📋', label: 'In Bearbeitung', at: o.processing_at });
                      if (o.tracking_added_at) events.push({ icon: '🔖', label: `Tracking hinterlegt (${o.tracking_number || '—'})`, at: o.tracking_added_at });
                      if (o.shipped_at) events.push({ icon: '📦', label: 'Versandt', at: o.shipped_at });
                      if (o.delivered_at) events.push({ icon: '✅', label: 'Zugestellt', at: o.delivered_at });
                      if (o.cancelled_at) events.push({ icon: '❌', label: 'Storniert', at: o.cancelled_at });
                      if (o.refunded_at) events.push({ icon: '💸', label: 'Erstattet', at: o.refunded_at });
                      if (o.archived_at) events.push({ icon: '📁', label: 'Archiviert', at: o.archived_at });

                      // Sort by timestamp
                      events.sort((a, b) => (a.at || '').localeCompare(b.at || ''));

                      if (!events.length) return <p className="text-xs text-gray-500 italic">Noch keine Ereignisse.</p>;

                      return events.map((e, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs" data-testid={`timeline-event-${i}`}>
                          <span className="w-5 text-center">{e.icon}</span>
                          <div className="flex-1">
                            <p className="text-[#5D4E3C]">{e.label}</p>
                            {e.at && (
                              <p className="text-[10px] text-[#8B7355]">
                                {new Date(e.at).toLocaleDateString('de-DE')} · {new Date(e.at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            )}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>

                {/* Document Actions */}
                <div className="border-t pt-3">
                  <p className="text-sm font-medium mb-2">Dokumente</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadOrderPdf(selectedOrder.order.id, 'confirmation', selectedOrder.order.order_number)}
                      data-testid="order-doc-confirmation"
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      Bestätigung
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadInvoicePdfForOrder(selectedOrder.order.id, selectedOrder.order.order_number)}
                      data-testid="order-doc-invoice"
                    >
                      <Receipt className="h-4 w-4 mr-1" />
                      Rechnung
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadOrderPdf(selectedOrder.order.id, 'delivery_note', selectedOrder.order.order_number)}
                      data-testid="order-doc-delivery"
                    >
                      <Truck className="h-4 w-4 mr-1" />
                      Lieferschein
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openOrderEmailDialog(selectedOrder.order)}
                      className="bg-amber-50/70 border-[#C4A084] hover:bg-[#E8DCC8] text-[#5D4E3C]"
                      data-testid="order-doc-email"
                    >
                      <Mail className="h-4 w-4 mr-1" />
                      Per E-Mail
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-12 text-center text-gray-500">
                <Package className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p>Wählen Sie eine Bestellung aus</p>
              </CardContent>
            </Card>
          )}
        </div>
        </div>
      </div>

      <SendByEmailDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        files={emailFiles}
        defaultSubject={emailDefaults.subject}
        defaultMessage={emailDefaults.message}
        defaultRecipient={emailDefaults.recipient}
        templateVars={emailDefaults.vars || {}}
      />
    </div>
  );
};

export default OrdersManager;
